import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { clientApi } from '../../services/clientService';

// Async thunk for fetching clients
export const fetchClients = createAsyncThunk(
  'client/fetchClients',
  async ({ reset = false, search = '', sort, skip = 0, limit = 100 }, { rejectWithValue }) => {
    try {
      const response = await clientApi.getClients({
        search, sort, skip, limit,
      });
      return { response, reset, search, skip, limit };
    } catch (error) {
      console.error('Error fetching clients:', error);
      return rejectWithValue(error.message || 'Failed to fetch clients');
    }
  }
);

// Async thunk for onboarding a new client
export const onboardClient = createAsyncThunk(
  'client/onboardClient',
  async (clientData, { rejectWithValue }) => {
    try {
      const response = await clientApi.onboardClient(clientData);
      return response;
    } catch (error) {
      console.error('Error onboarding client:', error);
      return rejectWithValue(error.message || 'Failed to onboard client');
    }
  }
);

// Async thunk for updating a client
export const updateClient = createAsyncThunk(
  'client/updateClient',
  async ({ clientId, clientData }, { rejectWithValue }) => {
    try {
      const response = await clientApi.updateClient(clientId, clientData);
      return response;
    } catch (error) {
      console.error('Error updating client:', error);
      return rejectWithValue(error.message || 'Failed to update client');
    }
  }
);

// Async thunk for deactivating a client
export const deactivateClient = createAsyncThunk(
  'client/deactivateClient',
  async (clientId, { rejectWithValue }) => {
    try {
      const response = await clientApi.deactivateClient(clientId);
      return response;
    } catch (error) {
      console.error('Error deactivating client:', error);
      return rejectWithValue(error.message || 'Failed to deactivate client');
    }
  }
);

// Async thunk for deleting a client
export const deleteClient = createAsyncThunk(
  'client/deleteClient',
  async (clientId, { rejectWithValue }) => {
    try {
      const response = await clientApi.deleteClient(clientId);
      return response;
    } catch (error) {
      console.error('Error deactivating client:', error);
      return rejectWithValue(error.message || 'Failed to deactivate client');
    }
  }
);

const initialState = {
  clients: [],
  loading: false,
  error: null,
  onboardLoading: false,
  onboardError: null,
  totalCount: 0,
  search: ''
};

const clientSlice = createSlice({
  name: 'client',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
      state.onboardError = null;
    },
    clearClients: (state) => {
      state.clients = [];
      state.lastKey = null;
    },
    setClientSearch: (state, action) => {
      state.search = action.payload;
    }
  },
  extraReducers: (builder) => {
    // Fetch clients
    builder
      .addCase(fetchClients.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchClients.fulfilled, (state, action) => {
        state.loading = false;
        state.clients = action.payload.response.items;
        state.totalCount = action.payload.response.totalCount;
        state.search = action.payload.search;
        state.error = null;
      })
      .addCase(fetchClients.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Onboard client
    builder
      .addCase(onboardClient.pending, (state) => {
        state.onboardLoading = true;
        state.onboardError = null;
      })
      .addCase(onboardClient.fulfilled, (state) => {
        state.onboardLoading = false;
        state.onboardError = null;
      })
      .addCase(onboardClient.rejected, (state, action) => {
        state.onboardLoading = false;
        state.onboardError = action.payload;
      });

    // Update client
    builder
      .addCase(updateClient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateClient.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(updateClient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Deactivate client
    builder
      .addCase(deactivateClient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deactivateClient.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(deactivateClient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Delete client
    builder
      .addCase(deleteClient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteClient.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(deleteClient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, clearClients, setClientSearch } = clientSlice.actions;
export default clientSlice.reducer;
